import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getUpdatedLastContactTimestamp, LAST_CONTACT_FALLBACK_MODE } from "../../../utils/index.js";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    SproutRecentPortHistoryFieldsWithDefaults,
    SproutRecentPortHistorySchemaTypes,
} from "../../types/sproutRecentPortHistory.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "SproutRecentPortHistory" as const;

// TODO: convert to responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SproutRecentPortHistory>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutRecentPortHistory extends SchemaBase<
    SproutRecentPortHistory,
    SproutRecentPortHistoryFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.number(), responseOnly: true })
    @prop({ type: () => Number, required: true })
    port!: number;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: () => new Date(0) })
    lastUplinkDate: Date = new Date(0);

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true, default: "NONE" })
    lastUplinkBytes: string = "NONE";

    /**
     * The date of our last attempt to downlink to this port. Set to epoch
     * if we have never downlinked to this port
     * @type {number}
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: () => new Date(0) })
    lastSentDownlinkDate: Date = new Date(0);

    /**
     * The bytes we most recently sent to the device, may or may not have been recieved
     * "NONE" if no bytes have been sent
     * @type {string}
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true, default: "NONE" })
    lastSentBytes: string = "NONE";

    /**
     * The idempotency token of the most recently sent downlink to this port. May or may not have been recieved.
     * 0 if no idemp has been sent
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    lastSentIdemp: number = 0;

    /**
     * The date of our last successful attempt to downlink to this port
     * Epoch if we have never successfully downlinked to this port
     * @type {number}
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: () => new Date(0) })
    lastEchoedDate: Date = new Date(0);

    /**
     * The bytes that were most recently received by the device on this port.
     * "NONE" if no idemp has ever been received after a downlink was sent on this port
     * @type {number}
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true, default: "NONE" })
    lastEchoedBytes: string = "NONE";

    /**
     * The idempotency token of the most recently received downlink on this port.
     * 0 if no idemp has ever been received after a downlink was sent on this port
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    lastEchoedIdemp: number = 0;

    static getters = {} as const satisfies Record<string, (self: SproutRecentPortHistory) => any>;

    static utilities = {
        /**
         * Safely updates or creates an entry in the lastContactByPort map for a given port.
         * Handles map initialization, entry creation, and property updates in a single call.
         *
         * @param map - The lastContactByPort map (will be created if undefined)
         * @param port - The port number to update
         * @param options - Optional fields to update
         * @param options.uplinkDate - The date of the uplink
         * @param options.uplinkBytes - The bytes received in the uplink
         * @param options.lastSentDownlinkDate - The date of the last sent downlink
         * @param options.lastSentBytes - The bytes of the last sent downlink
         * @param options.lastSentIdemp - The idempotency token of the last sent downlink
         * @param options.lastEchoedDate - The date of the last echoed (received) downlink
         * @param options.lastEchoedBytes - The bytes of the last echoed downlink
         * @param options.lastEchoedIdemp - The idempotency token of the last echoed downlink
         * @returns The updated map
         *
         * @example
         * // Update uplink information
         * device.gen4NetworkInfo.lastContactByPort = SproutRecentPortHistory.utilities.updatePortContact(
         *     device.gen4NetworkInfo.lastContactByPort,
         *     42,
         *     { uplinkDate: new Date(), uplinkBytes: [1, 2, 3] }
         * );
         *
         * @example
         * // Update echoed downlink information
         * networkInfo.lastContactByPort = SproutRecentPortHistory.utilities.updatePortContact(
         *     networkInfo.lastContactByPort,
         *     downlink.fPort,
         *     { lastEchoedDate: downlink.date, lastEchoedBytes: downlink.bytes, lastEchoedIdemp: downlink.idemp }
         * );
         */
        updatePortContact: (
            map: Map<string, SproutRecentPortHistory> | undefined,
            port: number | undefined,
            options?: {
                uplinkDate?: Date;
                uplinkBytes?: number[] | string;
                lastSentDownlinkDate?: Date;
                lastSentBytes?: string;
                lastSentIdemp?: number;
                lastEchoedDate?: Date;
                lastEchoedBytes?: string;
                lastEchoedIdemp?: number;
            },
        ): Map<string, SproutRecentPortHistory> => {
            // Initialize map if it doesn't exist
            if (!map) {
                map = new Map<string, SproutRecentPortHistory>();
            }

            // Exit early if no port provided
            if (port === undefined) {
                return map;
            }

            const portKey = port.toString();

            // Get existing entry or create new one
            let portEntry = map.get(portKey);
            if (!portEntry) {
                portEntry = new SproutRecentPortHistory({ port });
                map.set(portKey, portEntry);
            }

            // Update properties if provided
            if (options?.uplinkDate !== undefined) {
                portEntry.lastUplinkDate = getUpdatedLastContactTimestamp(
                    portEntry.lastUplinkDate,
                    options.uplinkDate,
                    LAST_CONTACT_FALLBACK_MODE.CURRENT_TIME,
                );
            }

            if (options?.uplinkBytes !== undefined) {
                portEntry.lastUplinkBytes = Array.isArray(options.uplinkBytes)
                    ? options.uplinkBytes.toString()
                    : options.uplinkBytes;
            }

            if (options?.lastSentDownlinkDate !== undefined) {
                portEntry.lastSentDownlinkDate = options.lastSentDownlinkDate;
            }

            if (options?.lastSentBytes !== undefined) {
                portEntry.lastSentBytes = options.lastSentBytes;
            }

            if (options?.lastSentIdemp !== undefined) {
                portEntry.lastSentIdemp = options.lastSentIdemp;
            }

            if (options?.lastEchoedDate !== undefined) {
                portEntry.lastEchoedDate = options.lastEchoedDate;
            }

            if (options?.lastEchoedBytes !== undefined) {
                portEntry.lastEchoedBytes = options.lastEchoedBytes;
            }

            if (options?.lastEchoedIdemp !== undefined) {
                portEntry.lastEchoedIdemp = options.lastEchoedIdemp;
            }

            return map;
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutRecentPortHistorySchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SproutRecentPortHistory, SproutRecentPortHistoryFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutRecentPortHistory.dtoValidators = getZodSchemaFromClass(
    SproutRecentPortHistory,
) as unknown as DtoValidatorTypes<SproutRecentPortHistorySchemaTypes>;
